/**
 * The menu half of a downshift-driven select: the floating panel and its rows.
 *
 * Shared by every list select in the app (the composer's deployment picker, the dashboard
 * filter controls). Each caller keeps its own trigger and downshift wiring — a combobox
 * with a search field is not a multi-select toggle list — but the panel, the scroll area
 * and the rows are the same everywhere. Callers size the panel by extending it.
 */
import { spacing } from '@scality/core-ui';
import styled from 'styled-components';
import { selectMenuFloatingPanel, selectMenuItemBase } from './Select.styles';

/**
 * Floating menu surface. Stays mounted while closed (hidden, not `display: none`) so
 * Floating UI keeps a box to measure; position comes from its `floatingStyles`.
 * Callers add width / max-height / padding.
 */
export const SelectMenuPanel = styled.div<{ $isOpen: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  ${selectMenuFloatingPanel}
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.$isOpen ? 'auto' : 'none')};
  transition: opacity 120ms ease;
`;

/** Scrollable region holding the list, so a long list scrolls under a fixed search box. */
export const SelectMenuScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${spacing.r4};
`;

/**
 * One row: label on the left, an optional trailing marker (e.g. a check) on the right.
 * `$multi` pulls the label against the checkbox leading it, so the pair reads as one.
 * `$disabled` dims a row that cannot be picked, and it never lights up.
 */
export const SelectMenuRow = styled.li<{ $highlighted: boolean; $multi?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$multi ? 'flex-start' : 'space-between')};
  gap: ${spacing.r8};
  ${selectMenuItemBase}
  color: ${(props) => props.theme.textPrimary};
  /* The sidebar's row blue, so picking from a menu reads the same as picking from the rail. */
  background-color: ${(props) => (props.$highlighted && !props.$disabled ? props.theme.chatCardBlue : 'transparent')};
  opacity: ${(props) => (props.$disabled ? 0.4 : 1)};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};

  &:hover {
    background-color: ${(props) => (props.$disabled ? 'transparent' : props.theme.chatCardBlue)};
  }
`;

/**
 * Holds the checkbox leading a multi-select row. It only reports state — an interactive one
 * would fire its own change *and* bubble to the row, toggling twice and netting nothing.
 */
export const SelectRowCheckbox = styled.span`
  display: flex;
  align-items: center;
  pointer-events: none;
`;
